/**
 * QuoterCart - Bridge entre el cotizador y Shopify Cart API
 *
 * Funciones principales:
 * - Agregar items del cotizador al carrito de Shopify
 * - Guardar sessionId y datos del cotizador en line_item properties
 * - Sincronizar entre carrito Shopify y sessionStorage (fallback)
 * - Exponer items del cotizador para el checkout
 *
 * v1.0 - Carrito Híbrido
 */

const QuoterCart = {
  // Clave para sessionStorage (fallback)
  STORAGE_KEY: 'quoter_cart_items',

  // Prefijo para identificar items del cotizador en Shopify
  QUOTER_PROPERTY_KEY: '_quoter_data',

  // Cache de items
  _items: null,

  // Timer para debounce del listener de cambios del tema (cart:change)
  _cartChangeDebounce: null,

  /**
   * Inicializar el carrito
   * Sincroniza datos entre Shopify y sessionStorage
   */
  async init() {
    try {
      await this.syncFromShopify();
    } catch (error) {
      this._loadFromStorage();
    }

    // Escuchar cambios en otras pestañas
    window.addEventListener('storage', (event) => {
      if (event.key === this.STORAGE_KEY) {
        this._loadFromStorage();
        this._dispatchCartUpdate();
      }
    });

    // Fix 1 (C2): higiene en vivo del carrito híbrido.
    // El botón "Eliminar" del drawer usa el borrado nativo de Shopify y NO pasa
    // por QuoterCart.removeItem(), así que _items queda stale y los diseños en
    // R2 no se limpian. El tema HDT (Halo) emite "cart:change" en document en
    // cada alta/baja/edición de line items (ver assets/custom.js:1922 y
    // assets/global.min.js), con detail.cart. Escuchamos ese evento para
    // re-sincronizar y hacer el rollback de los diseños removidos.
    document.addEventListener('cart:change', () => this._onThemeCartChange());

    return this;
  },

  /**
   * Agregar item del cotizador al carrito
   * @param {Object} itemData - Datos del item del cotizador
   * @param {string} itemData.producto - ID del producto (premium, holografico, economico, iman, sheets, sheets_economicas)
   * @param {number} itemData.ancho - Ancho en cm
   * @param {number} itemData.alto - Alto en cm
   * @param {number} itemData.cantidad - Cantidad de piezas
   * @param {Array} itemData.disenos - Array de diseños [{nombre, url}]
   * @param {string} itemData.sessionId - ID de sesión para agrupar archivos
   * @param {number} itemData.precioTotal - Precio total calculado
   * @param {number} itemData.precioUnitario - Precio unitario con IVA
   * @param {string} itemData.indicaciones - Indicaciones especiales (opcional)
   * @param {string} itemData.laminado - Laminado elegido (opcional; solo premium/sheets)
   * @param {string} itemData.forma - Forma elegida (opcional)
   * @returns {Promise<Object>} Item agregado con ID único
   */
  async addItem(itemData) {
    const {
      producto,
      ancho,
      alto,
      cantidad,
      disenos = [],
      sessionId,
      precioTotal,
      precioUnitario,
      indicaciones = '',
      laminado = '',
      forma = '',
    } = itemData;

    // Validar datos requeridos
    if (!producto || !ancho || !alto || !cantidad || !sessionId) {
      throw new Error('Faltan datos requeridos para agregar al carrito');
    }

    // Obtener datos de imagen y URL del producto
    const productImageData = window.QuoterProductImages
      ? window.QuoterProductImages.getProductData(producto)
      : { imageUrl: null, color: '#E5E7EB', name: producto, icon: null };

    const productUrl = window.QuoterUrls
      ? window.QuoterUrls.getProductPageUrl(producto)
      : '/';

    // Crear item con ID único
    const item = {
      id: `quoter_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
      producto,
      ancho: parseFloat(ancho),
      alto: parseFloat(alto),
      cantidad: parseInt(cantidad),
      disenos,
      sessionId,
      precioTotal: parseFloat(precioTotal),
      precioUnitario: parseFloat(precioUnitario),
      indicaciones,
      laminado,
      forma,
      createdAt: new Date().toISOString(),
      // Datos de imagen y navegación
      imagenUrl: productImageData.imageUrl,
      colorProducto: productImageData.color,
      nombreProducto: productImageData.name,
      productUrl: productUrl,
    };

    // Obtener Variant ID del producto para Shopify
    const variantId = this._getVariantId(producto);

    if (!variantId) {
      throw new Error(`No se encontró variant ID para producto: ${producto}`);
    }

    try {
      // Agregar al carrito de Shopify con properties
      const shopifyResponse = await this._addToShopifyCart(variantId, item);

      // Guardar en sessionStorage como fallback
      this._addToStorage(item);

      // Invalidar la orden pendiente tras el éxito de AMBAS escrituras (Shopify +
      // sessionStorage). Simétrico al Caso 2 de la guarda de _syncAfterThemeChange:
      // este alta usa fetch directo a /cart/add.js, que NO emite "cart:change", así
      // que el listener del tema no se entera del cambio de carrito. Sin esto, un
      // pending order previo (Draft Order no pagada, aún vigente dentro del TTL de 2h)
      // sobreviviría y processCheckout redirigiría al invoice VIEJO, que no incluye
      // este item nuevo. NO borramos R2: los diseños del draft viejo siguen
      // referenciados por esa Draft Order; solo forzamos que el próximo checkout
      // cree un draft nuevo con los items actuales.
      if (window.QuoterCheckout && typeof window.QuoterCheckout._clearPendingOrder === 'function') {
        window.QuoterCheckout._clearPendingOrder();
      }

      // Notificar cambio
      this._dispatchCartUpdate();

      // Emitir evento específico de item agregado
      if (window.EventBus) {
        window.EventBus.emit(window.QUOTER_EVENTS?.CART_ITEM_ADDED || 'quoter:cart:itemAdded', {
          item,
          cartTotal: this.getTotal(),
          itemCount: this.getItemCount()
        });
      }

      return { item, shopifyResponse };

    } catch (error) {
      // Intentar rollback de diseños en R2 si hay error
      if (sessionId && window.AvexrhAPI) {
        try {
          await window.AvexrhAPI.deleteSession(sessionId);
        } catch (rollbackError) {
          // Rollback silencioso
        }
      }

      throw error;
    }
  },

  /**
   * Obtener todos los items del cotizador en el carrito
   * @returns {Array} Items del cotizador
   */
  getItems() {
    if (this._items === null) {
      this._loadFromStorage();
    }
    return this._items || [];
  },

  /**
   * Obtener item por ID
   * @param {string} itemId - ID del item
   * @returns {Object|null} Item o null si no existe
   */
  getItem(itemId) {
    const items = this.getItems();
    return items.find(item => item.id === itemId) || null;
  },

  /**
   * Eliminar item del carrito
   * @param {string} itemId - ID del item a eliminar
   * @returns {Promise<boolean>} true si se eliminó
   */
  async removeItem(itemId) {
    const item = this.getItem(itemId);
    if (!item) {
      return false;
    }

    try {
      await this._removeFromShopifyCart(item);
      this._removeFromStorage(itemId);

      // Limpiar orden pendiente: el carrito cambió, la orden anterior ya no aplica
      if (window.QuoterCheckout) {
        window.QuoterCheckout._clearPendingOrder();
      }

      // Eliminar diseños de R2
      if (item.sessionId && window.AvexrhAPI) {
        try {
          await window.AvexrhAPI.deleteSession(item.sessionId);
        } catch (error) {
          // Error silencioso
        }
      }

      this._items = this._items.filter(i => i.id !== itemId);
      this._dispatchCartUpdate();

      if (window.EventBus) {
        window.EventBus.emit(window.QUOTER_EVENTS?.CART_ITEM_REMOVED || 'quoter:cart:itemRemoved', {
          itemId,
          cartTotal: this.getTotal(),
          itemCount: this.getItemCount()
        });
      }

      return true;

    } catch (error) {
      throw error;
    }
  },

  /**
   * Limpiar todos los items del cotizador del carrito
   * @returns {Promise<void>}
   */
  async clearItems() {
    const items = this.getItems();

    for (const item of items) {
      try {
        await this.removeItem(item.id);
      } catch (error) {
        // Error silencioso
      }
    }

    this._items = [];
    this._saveToStorage();
    this._dispatchCartUpdate();

    // Limpiar la orden pendiente: el carrito está vacío, no tiene sentido
    // seguir redirigiendo a un checkout que ya no corresponde al carrito actual
    if (window.QuoterCheckout) {
      window.QuoterCheckout._clearPendingOrder();
    }
  },

  /**
   * Obtener conteo de items
   * @returns {number} Número de items del cotizador
   */
  getItemCount() {
    return this.getItems().length;
  },

  /**
   * Obtener total del carrito (solo items del cotizador)
   * @returns {number} Total en pesos
   */
  getTotal() {
    const items = this.getItems();
    return items.reduce((sum, item) => sum + (item.precioTotal || 0), 0);
  },

  /**
   * Verificar si el carrito tiene items del cotizador
   * @returns {boolean}
   */
  hasQuoterItems() {
    return this.getItemCount() > 0;
  },

  /**
   * Exportar items para el checkout (formato para API)
   * @returns {Array} Items formateados para draft-order-multi
   */
  exportForCheckout() {
    const items = this.getItems();

    return items.map(item => ({
      producto: item.producto,
      ancho: item.ancho,
      alto: item.alto,
      cantidad: item.cantidad,
      disenos: item.disenos,
      sessionId: item.sessionId,
      // Incluir precios custom para el Draft Order
      precioTotal: item.precioTotal,
      precioUnitario: item.precioUnitario,
      indicaciones: item.indicaciones || '',
      laminado: item.laminado || '',
      forma: item.forma || '',
    }));
  },

  /**
   * Sincronizar items desde el carrito de Shopify
   */
  async syncFromShopify() {
    const response = await fetch('/cart.js');
    const cart = await response.json();

    // Filtrar items que tienen datos del cotizador
    const quoterItems = [];

    for (const lineItem of cart.items) {
      if (lineItem.properties && lineItem.properties[this.QUOTER_PROPERTY_KEY]) {
        try {
          const quoterData = JSON.parse(lineItem.properties[this.QUOTER_PROPERTY_KEY]);
          quoterData._shopifyLineItemKey = lineItem.key;
          quoterItems.push(quoterData);
        } catch (e) {
          // Error silencioso - item corrupto
        }
      }
    }

    this._items = quoterItems;
    this._saveToStorage();

    return quoterItems;
  },

  // ============================================
  // MÉTODOS PRIVADOS
  // ============================================

  /**
   * Handler del evento "cart:change" del tema HDT.
   * Aplica un debounce corto porque el tema puede emitir varios cart:change
   * seguidos (re-render del drawer + section rendering) por una sola acción.
   */
  _onThemeCartChange() {
    clearTimeout(this._cartChangeDebounce);
    this._cartChangeDebounce = setTimeout(() => {
      this._syncAfterThemeChange();
    }, 400);
  },

  /**
   * Re-sincronizar tras un cambio del carrito hecho desde el tema (drawer).
   * Detecta los items del cotizador que se removieron y hace el rollback de
   * sus diseños en R2, respetando la guarda de checkout/orden pendiente.
   */
  async _syncAfterThemeChange() {
    // Snapshot ANTES del sync para detectar qué items se removieron.
    const previousItems = this.getItems().slice();

    try {
      await this.syncFromShopify();
    } catch (error) {
      // Si /cart.js falla, conservamos el estado local (no tocamos nada).
      console.warn('[QuoterCart] No se pudo re-sincronizar tras cart:change:', error);
      return;
    }

    // Items del cotizador que ya NO están en el carrito de Shopify.
    const currentSessionIds = new Set(
      (this._items || []).map(i => i.sessionId).filter(Boolean)
    );
    const removedItems = previousItems.filter(
      item => item.sessionId && !currentSessionIds.has(item.sessionId)
    );

    if (removedItems.length === 0) {
      return;
    }

    // GUARDA CASO 0 (Fix 3 / A1): si NOSOTROS mismos acabamos de vaciar el
    // carrito de Shopify tras crear la Draft Order (QuoterCheckout._clearShopifyCart),
    // ignorar este evento por completo. El /cart/clear.js directo normalmente NO
    // emite cart:change, pero protegemos una ventana de ~5s por si un re-render
    // tardío del tema lo emitiera después de que el finally reseteó _isProcessing
    // a false: sin esta guarda caería en el Caso 2 y borraría el pending order
    // recién guardado (el mecanismo para volver al invoice dentro de 2h).
    const selfClearAt = window.QuoterCheckout && window.QuoterCheckout._selfCartClearAt;
    if (selfClearAt && (Date.now() - selfClearAt) < 5000) {
      return;
    }

    // GUARDA CRÍTICA (interacción con Fix 3): distinguimos 3 casos.
    //
    // 1) checkoutInProgress (QuoterCheckout._isProcessing): hay un checkout en
    //    curso que puede estar limpiando el carrito de Shopify tras crear la
    //    Draft Order. NO tocamos nada: los diseños de R2 quedan referenciados
    //    por la orden y el pending order se necesita para completar el flujo.
    //    Solo re-sincronizamos (ya hecho arriba) y salimos.
    //
    // 2) pendingOrderActive sin checkout en curso: existe una Draft Order previa
    //    (bug C2) que el usuario no pagó y ahora editó/eliminó items desde el
    //    drawer. NO borramos R2 (esos diseños siguen referenciados por la Draft
    //    Order existente en Shopify), PERO SÍ invalidamos el pending order: el
    //    carrito cambió, el invoice viejo ya no representa la compra y el próximo
    //    checkout debe crear una Draft Order nueva con los items actuales (si no,
    //    processCheckout redirigiría al invoice viejo dentro del TTL de 2h).
    //
    // 3) Caso normal (ni checkout ni pending): rollback de R2 de los items
    //    removidos + limpiar pending order + notificar a la UI.
    const checkoutInProgress = !!(window.QuoterCheckout && window.QuoterCheckout._isProcessing);
    const pendingOrderActive = !!(
      window.QuoterCheckout &&
      typeof window.QuoterCheckout._getPendingOrder === 'function' &&
      window.QuoterCheckout._getPendingOrder()
    );

    // Caso 1: checkout en curso -> no tocar nada.
    if (checkoutInProgress) {
      return;
    }

    // Caso 2: pending order activo (sin checkout) -> NO borrar R2, pero SÍ
    // invalidar el pending order para forzar una Draft Order nueva.
    if (pendingOrderActive) {
      if (window.QuoterCheckout && typeof window.QuoterCheckout._clearPendingOrder === 'function') {
        window.QuoterCheckout._clearPendingOrder();
      }
      this._dispatchCartUpdate();
      return;
    }

    // Caso 3: el usuario editó/eliminó items desde el drawer.
    // Rollback de diseños en R2 para cada item removido (fallo no crítico).
    for (const item of removedItems) {
      if (item.sessionId && window.AvexrhAPI) {
        try {
          await window.AvexrhAPI.deleteSession(item.sessionId);
        } catch (error) {
          console.warn('[QuoterCart] No se pudo eliminar la sesión de R2:', item.sessionId, error);
        }
      }
    }

    // El carrito cambió: la Draft Order previa (si la hubiera) ya no aplica.
    if (window.QuoterCheckout && typeof window.QuoterCheckout._clearPendingOrder === 'function') {
      window.QuoterCheckout._clearPendingOrder();
    }

    // Notificar a la UI del cotizador el nuevo estado del carrito.
    this._dispatchCartUpdate();
  },

  /**
   * Obtener variant ID de Shopify para un producto
   */
  _getVariantId(producto) {
    // Mapeo de productos a variant IDs
    // Estos IDs deben configurarse en window.QUOTER_VARIANT_IDS
    const variantIds = window.QUOTER_VARIANT_IDS || {
      premium: null,
      holografico: null,
      economico: null,
      iman: null,
      sheets: null,
      sheets_economicas: null,
    };

    // Alias legacy: items pre-migración (ago 2026) traen producto 'vinyl'.
    const id = producto === 'vinyl' ? 'premium' : producto;
    return variantIds[id] || variantIds[producto] || variantIds.premium || variantIds.vinyl;
  },

  /**
   * Agregar item al carrito de Shopify
   * Con retry automático en caso de error
   * Incluye Section Rendering API para actualizar el drawer
   */
  async _addToShopifyCart(variantId, itemData) {
    const properties = {
      [this.QUOTER_PROPERTY_KEY]: JSON.stringify(itemData),
      '_quoter_price': Math.round(itemData.precioTotal * 100), // Precio en centavos para Liquid
      'Producto': this._getProductName(itemData.producto),
      'Dimensiones': `${itemData.ancho} × ${itemData.alto} cm`,
      'Cantidad': `${itemData.cantidad} piezas`,
      'Session ID': itemData.sessionId,
    };

    // Agregar nombres de diseños (o marcar como pendiente si no subieron ninguno)
    if (itemData.disenos && itemData.disenos.length > 0) {
      properties['Diseños'] = itemData.disenos.map(d => d.nombre).join(', ');

      // URL individual por diseño
      itemData.disenos.forEach((diseno, i) => {
        if (diseno.url && diseno.url.startsWith('http')) {
          const num = String(i + 1).padStart(2, '0');
          properties[`Artwork ${num} URL`] = diseno.url;
        }
      });
    } else {
      properties['Diseños'] = 'Pendiente';
    }

    // Laminado y forma solo si el cliente los eligió
    if (itemData.laminado) {
      properties['Laminado'] = itemData.laminado;
    }
    if (itemData.forma) {
      properties['Forma'] = itemData.forma;
    }

    // Agregar indicaciones si existen
    if (itemData.indicaciones) {
      properties['Indicaciones'] = itemData.indicaciones;
    }

    // Recopilar secciones que necesitan actualizarse (patrón del tema HDT)
    const bundleSections = [];
    document.dispatchEvent(new CustomEvent('cart:push:section.id', {
      bubbles: true,
      detail: { sections: bundleSections }
    }));

    // Retry logic: 3 intentos con backoff exponencial
    const maxRetries = 3;
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: properties,
            // Section Rendering API: obtener HTML actualizado del drawer
            sections: bundleSections.join(','),
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || 'Error agregando al carrito de Shopify');
        }

        const cart = await response.json();

        // Emitir evento de sincronización exitosa
        if (window.EventBus) {
          window.EventBus.emit(window.QUOTER_EVENTS?.SYNC_SUCCESS || 'quoter:sync:success', {
            item: itemData,
            attempt: attempt
          });
        }

        return cart;

      } catch (error) {
        lastError = error;

        // Si no es el último intento, esperar antes de reintentar
        if (attempt < maxRetries) {
          const waitTime = 1000 * attempt; // 1s, 2s, 3s
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    }

    // Si llegamos aquí, todos los intentos fallaron
    if (window.EventBus) {
      window.EventBus.emit(window.QUOTER_EVENTS?.SYNC_ERROR || 'quoter:sync:error', {
        error: lastError.message,
        item: itemData
      });
    }

    throw lastError;
  },

  /**
   * Eliminar item del carrito de Shopify
   */
  async _removeFromShopifyCart(item) {
    if (!item._shopifyLineItemKey) {
      // Buscar el line item por sessionId
      const cart = await (await fetch('/cart.js')).json();

      for (const lineItem of cart.items) {
        if (lineItem.properties && lineItem.properties[this.QUOTER_PROPERTY_KEY]) {
          try {
            const data = JSON.parse(lineItem.properties[this.QUOTER_PROPERTY_KEY]);
            if (data.id === item.id || data.sessionId === item.sessionId) {
              item._shopifyLineItemKey = lineItem.key;
              break;
            }
          } catch (e) {
            // Ignorar
          }
        }
      }
    }

    if (item._shopifyLineItemKey) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: item._shopifyLineItemKey,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        throw new Error('Error eliminando del carrito de Shopify');
      }

      return response.json();
    }
  },

  /**
   * Obtener nombre legible del producto
   */
  _getProductName(producto) {
    // Usar QuoterProductImages si está disponible
    if (window.QuoterProductImages) {
      return window.QuoterProductImages.getName(producto);
    }

    // Fallback
    const names = {
      premium: 'Sticker Premium',
      vinyl: 'Sticker Premium', // alias legacy pre-migración ago-2026
      holografico: 'Sticker Holográfico',
      economico: 'Sticker Económico',
      iman: 'Imán Impreso',
      sheets: 'Sticker Sheets Laminadas',
      sheets_economicas: 'Sticker Sheets Económicas',
    };
    return names[producto] || producto;
  },

  /**
   * Cargar items desde sessionStorage
   */
  _loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      this._items = stored ? JSON.parse(stored) : [];
    } catch (e) {
      this._items = [];
    }
  },

  /**
   * Guardar items en sessionStorage
   */
  _saveToStorage() {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this._items || []));
    } catch (e) {
      // Error silencioso
    }
  },

  /**
   * Agregar item a sessionStorage
   */
  _addToStorage(item) {
    this._loadFromStorage();
    this._items.push(item);
    this._saveToStorage();
  },

  /**
   * Eliminar item de sessionStorage
   */
  _removeFromStorage(itemId) {
    this._loadFromStorage();
    this._items = this._items.filter(i => i.id !== itemId);
    this._saveToStorage();
  },

  /**
   * Disparar evento de actualización del carrito
   * Usa EventBus si está disponible, con fallback a CustomEvent
   */
  _dispatchCartUpdate() {
    const cartData = {
      items: this.getItems(),
      count: this.getItemCount(),
      total: this.getTotal(),
      timestamp: Date.now()
    };

    // Usar EventBus si está disponible
    if (window.EventBus) {
      window.EventBus.emit(window.QUOTER_EVENTS?.CART_UPDATED || 'quoter:cart:updated', cartData);
    }

    // Mantener CustomEvent para compatibilidad con código existente
    const event = new CustomEvent('quoter:cart:update', {
      detail: cartData,
    });
    window.dispatchEvent(event);

    // Actualizar el badge del carrito del tema HDT
    this._updateThemeCartCount();
  },

  /**
   * Actualizar el contador del carrito del tema HDT
   * Hace fetch a /cart.js para obtener el count real y actualiza <hdt-cart-count>
   */
  async _updateThemeCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const count = cart.item_count;

      document.querySelectorAll('hdt-cart-count').forEach(el => {
        el.textContent = count;
      });
    } catch (error) {
      // Error silencioso - no bloquear el flujo principal
    }
  },
};

// Exportar globalmente
if (typeof window !== 'undefined') {
  window.QuoterCart = QuoterCart;

  // Auto-inicializar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => QuoterCart.init());
  } else {
    QuoterCart.init();
  }
}
